import React from 'react';

import { useIntl } from 'react-intl';

import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import { useCreateInvitation } from 'src/features/users/api/useCreateInvitation';
import { LIST_INVITATIONS_RESOURCE } from 'src/features/users/api/useListInvitations';
import InvitationFormModal from 'src/features/users/components/forms/InvitationFormModal';
import { userMessages } from 'src/features/users/utils/messages';
import { generalErrorMessages } from 'src/utils/messages';

interface CreateInvitationFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    children?: React.ReactNode;
}

function CreateInvitationFormModal<TData>({ className }: CreateInvitationFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useCreateInvitation({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_INVITATIONS_RESOURCE] });
            toast({
                title: formatMessage(userMessages.invitationCreateSuccess),
                description: formatMessage(userMessages.invitationCreateSuccessDetails, { email: success.email }),
            });
        },
        onError: (error) => {
            const emailError = error.response?.data?.email;

            const emailErrorDescription = emailError?.includes('Invitation already sent!')
                ? userMessages.invitationAlreadySent
                : emailError?.includes('Invitation already accepted!')
                  ? userMessages.invitationAlreadyAccepted
                  : generalErrorMessages.somethingWentWrongRetry;

            toast({
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: formatMessage(emailErrorDescription),
                variant: 'destructive',
            });
        },
    });

    return (
        <InvitationFormModal
            className={className}
            mutate={(values, onSuccess) => mutate({ ...values, parkingId }, { onSuccess })}
            title={formatMessage(generalMessages.createEntry, {
                entry: formatMessage(userMessages.invitation).toLowerCase(),
            })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(userMessages.newUserDescription)}
        />
    );
}

export default CreateInvitationFormModal;
